import { DateTime } from "luxon";
import type { entityProcessConfig } from "../entity-process/signalr-process-config";

export const webDomainValidator = (value: any) => {
  if (value == null || value === "") {
    return true;
  }
  if (value.split(".").length == 2) {
    //without subdomain like abc.com
    return /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,62}[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/.test(
      value,
    );
  } else {
    //with subdomain like abc.co.uk
    let prefix = value.slice(0, value.indexOf("."));
    if (prefix.length > 64) return false;

    return /^[a-zA-Z0-9]+([a-zA-Z0-9-]+\.[a-zA-Z0-9-]+){1,61}[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/.test(
      value,
    );
  }
};

//Time Spent
export const customTimeValidator = (value: any) => {
  if (value == "00:00") {
    return false;
  }
  return true;
};

export const validateTime = (value: any) => {
  if (value != null && value.length == 5) {
    const arr = value.split(":");
    const hours = parseInt(arr[0]);
    const mins = parseInt(arr[1]);
    if (mins > 59) {
      return false;
    }
    if (hours == 99) {
      if (mins > 59) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }
  return true;
};

export const validateMinutes = (value: any) => {
  if (value != null && value.length == 5) {
    const arr = value.split(":");
    const mins = parseInt(arr[1]);
    if (mins > 59) {
      return false;
    } else {
      return true;
    }
  }
  return true;
};

export const validateHours = (value: any) => {
  if (value != null && value.length == 5) {
    const arr = value.split(":");
    const hours = parseInt(arr[0]);
    if (hours > 23) {
      return false;
    } else {
      return true;
    }
  }
  return true;
};

export const moreThanOneItems = (value: any) => {
  if (value != null && value.length > 0) {
    let number = parseFloat(value);
    if (number == 0 || number == 0.0 || value == ".") {
      return false;
    }
    return true;
  }
  return true;
};

export const valueGreaterThanZero = (value: any) => {
  if (value <= 0 || value <= 0.0 || value == ".") {
    return false;
  }
  if (value != null && value.length > 0) {
    let number = parseFloat(value);
    if (number == 0 || number == 0.0 || value == ".") {
      return false;
    }
    return true;
  }
  return true;
};

export const valueEqualZero = (value: any) => {
  if (isNaN(parseFloat(value))) {
    return false;
  }

  if (value != null && value.length > 0) {
    let number = parseFloat(value);
    if (number == 0) {
      return false;
    }
    return true;
  }
  return true;
};

export const valueNotZero = (value: any) => {
  if (value != null && value.length > 0) {
    let number = parseFloat(value);
    if (number == 0 || number == 0.0 || value == ".") {
      return false;
    }
    return true;
  }
  return true;
};

export const acceptValidQuantity = (value: any) => {
  if (value != null && value.length > 0) {
    if (typeof value === "string") {
      const temp = parseInt(value);
      if (temp <= 100) {
        return true;
      }
      return false;
    } else {
      if (value <= 100) {
        return true;
      }
      return false;
    }
  }
  return true;
};

export const emailStandard = (value: any) => {
  if (value == null || value === "") {
    return true;
  }
  const s = String(value).trim();
  if (s.length === 0) {
    return true;
  }
  const at = s.indexOf("@");
  if (at <= 0 || at !== s.lastIndexOf("@") || at === s.length - 1) {
    return false;
  }
  const prefix = s.slice(0, at);
  if (prefix.length === 0 || prefix.length > 64) {
    return false;
  }
  const domain = s.slice(at + 1);
  return domain.length > 0;
};

export const customDateValidator = (value: string) => {
  if (value != null && value.length > 0) {
    if (value.length != 10) return false;

    let date = DateTime.fromFormat(value, "MM-dd-yyyy");

    if (
      date < DateTime.fromFormat("01-01-1920", "MM-DD-YYYY") ||
      date > DateTime.fromFormat("12-31-2120", "MM-DD-YYYY")
    ) {
      return false;
    }

    return date.isValid;
  }
  return true;
};

export const customDomainValidator = (value: string) => {
  if (value != null && value.length > 0) {
    if (value.split(".").length == 2) {
      //without subdomain like abc.com
      return /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,62}[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/.test(
        value,
      );
    } else {
      return false;
    }
  }
  return true;
};

export const positiveValue = (value: any) => {
  return value && value >= 0;
};

/** Plain text from TipTap HTML (ignores inline images and empty markup). */
export const getTiptapPlainText = (value: unknown): string => {
  if (value == null || value === "") {
    return "";
  }
  return String(value)
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&nbsp;/g, "")
    .trim();
};

/** Requires non-empty message text; inline images alone are not valid (matches New Request). */
export const tiptapValidation = (value: unknown) => {
  return getTiptapPlainText(value) !== "";
};

/** True when TipTap HTML contains at least one inline `<img>` (pasted or inserted). */
export const tiptapHasInlineImage = (value: unknown): boolean => {
  if (value == null || value === "") {
    return false;
  }
  return /<img\b/i.test(String(value));
};

/** Ticket detail reply: text and/or inline image is enough to send. */
export const tiptapCommentIsSubmittable = (value: unknown): boolean => {
  return tiptapValidation(value) || tiptapHasInlineImage(value);
};

export const extractEmailAndUrls = (value: string) => {
  if (!value) return "";

  let splitString = value.split(" ");
  let stringValue = "" as string;
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  const urlRegex =
    /([\w+]+\:\/\/)?([\w\d-]+\.)*[\w-]+[\.\:]\w+([\/\?\=\&\#\.]?[\w-]+)*\/?/gm;
  for (let arr of splitString) {
    let email = arr.match(emailRegex);
    if (email && email.length > 0) {
      //Has Email Succeed
      arr = `<a href="mailto:${arr}">${arr}</a>`;
    } else {
      let url = arr.match(urlRegex);
      if (url && url.length > 0) {
        //Has URL Succeed
        if (arr.indexOf("http") > -1) {
          arr = `<a href="${arr}" target="_blank">${arr}</a>`;
        } else if (arr.indexOf("https") > -1) {
          arr = `<a href="${arr}" target="_blank">${arr}</a>`;
        } else {
          arr = `<a href="https://${arr}" target="_blank">${arr}</a>`;
        }
      }
    }

    stringValue += " " + arr;
  }

  return stringValue;
};

export const entityResultWithAccessValidator = (
  result: any,
  object: entityProcessConfig | null,
) => {
  return result && result[0] && object && object.hasAccess;
};
